import React, { useState, useMemo, useEffect } from 'react';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../firebaseInit';
import { useNotification } from '../contexts/NotificationContext';
import * as firestorePaths from '../utils/firestorePaths';
import { getArtifactProjectId } from '../utils/firestorePaths';

const AdminTemplateBrowser = ({ onClose }) => {
  const { showSuccess, showError, showWarning, showInfo } = useNotification();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [allTemplates, setAllTemplates] = useState({});
  const [isLoading, setIsLoading] = useState(true);
    
  // Load all templates from all teams for admin browser
  useEffect(() => {
    const loadAllTemplates = async () => {
      try {
        setIsLoading(true);
        const appId = getArtifactProjectId();
        const templatesData = {};

        // 1. Load global/system templates
        const globalPath = firestorePaths.getGlobalTemplatesPath(appId);
        const globalQuery = query(collection(db, globalPath));
        const globalSnapshot = await getDocs(globalQuery);
        globalSnapshot.forEach(doc => {
          const templateData = doc.data();
          templatesData[doc.id] = { 
            ...templateData, 
            id: doc.id, 
            accessLevel: 'global',
            source: 'system',
            name: templateData?.metadata?.name || templateData?.name || `Template ${doc.id}`
          };
        });

        // 2. Load templates from ALL teams
        const teamsRoot = firestorePaths.getTeamsRoot(appId);
        const teamsQuery = query(collection(db, teamsRoot));
        const teamsSnapshot = await getDocs(teamsQuery);
                
        for (const teamDoc of teamsSnapshot.docs) {
          const teamId = teamDoc.id;
          const teamData = teamDoc.data();
                    
          try {
            const teamTemplatesPath = firestorePaths.getTemplatesPath(appId, teamId);
            const teamQuery = query(collection(db, teamTemplatesPath));
            const teamSnapshot = await getDocs(teamQuery);
                        
            teamSnapshot.forEach(doc => {
              const uniqueId = `team_${teamId}_${doc.id}`;
              const templateData = doc.data();
              templatesData[uniqueId] = { 
                ...templateData, 
                id: doc.id,
                uniqueId: uniqueId,
                accessLevel: 'team',
                source: 'team',
                teamId: teamId,
                teamName: teamData.name || teamId,
                name: templateData?.metadata?.name || templateData?.name || `Template ${doc.id}`
              };
            });
          } catch (error) {
            console.warn(`Could not load templates for team ${teamId}:`, error);
          }
        }

        setAllTemplates(templatesData);
        console.log('🔍 Admin Template Browser loaded', Object.keys(templatesData).length, 'templates');
      } catch (error) {
        console.error('❌ Error loading templates for admin browser:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAllTemplates();
  }, []);

  // Group templates by team
  const templatesByTeam = useMemo(() => {
    const groups = {};
        
    Object.entries(allTemplates).forEach(([id, template]) => {
      if (template.source === 'team' && template.teamName) {
        const teamName = template.teamName;
        if (!groups[teamName]) {
          groups[teamName] = [];
        }
        groups[teamName].push({ id, template });
      } else if (template.source === 'system') {
        // Add system templates to a special group
        if (!groups['System Templates']) {
          groups['System Templates'] = [];
        }
        groups['System Templates'].push({ id, template });
      }
    });

    return groups;
  }, [allTemplates]);

  // Get unique team names for filter
  const teamNames = useMemo(() => {
    return ['all', ...Object.keys(templatesByTeam).sort()];
  }, [templatesByTeam]);

  // Filter templates based on search and team selection
  const filteredTemplates = useMemo(() => {
    let filtered = {};
        
    Object.entries(templatesByTeam).forEach(([teamName, templates]) => {
      if (selectedTeam !== 'all' && selectedTeam !== teamName) return;
            
      templates.forEach(({ id, template }) => {
        const templateName = template.metadata?.name || template.name || '';
        const templateDescription = template.metadata?.description || template.description || '';
        const matchesSearch = templateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                    templateDescription.toLowerCase().includes(searchTerm.toLowerCase());
                
        if (matchesSearch || searchTerm === '') {
          filtered[id] = { ...template, teamName, originalId: id };
        }
      });
    });

    return filtered;
  }, [templatesByTeam, searchTerm, selectedTeam]);

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-4 w-full max-w-4xl max-h-[90vh] overflow-hidden">
          <div className="flex items-center justify-center py-8">
            <div className="text-lg text-gray-600">Loading templates...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-sm max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Admin Template Browser</h2>
            <p className="text-gray-600 mt-1">Browse templates from all teams</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Search Templates
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by template name or description..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="sm:w-64">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Filter by Team
              </label>
              <select
                value={selectedTeam}
                onChange={(e) => setSelectedTeam(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {teamNames.map(teamName => (
                  <option key={teamName} value={teamName}>
                    {teamName === 'all' ? 'All Teams' : teamName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {Object.keys(templatesByTeam).length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Templates Found</h3>
              <p className="text-gray-600">No team templates are available to browse.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(templatesByTeam).map(([teamName, templates]) => {
                const filteredTeamTemplates = templates.filter(({ id }) => 
                  Object.keys(filteredTemplates).includes(id)
                );

                if (filteredTeamTemplates.length === 0) return null;

                return (
                  <div key={teamName} className="border border-gray-200 rounded-lg">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {teamName}
                        <span className="ml-2 text-sm font-normal text-gray-600">
                                                    ({filteredTeamTemplates.length} template{filteredTeamTemplates.length !== 1 ? 's' : ''})
                        </span>
                      </h3>
                    </div>
                    <div className="p-4">
                      <div className="grid gap-3">
                        {filteredTeamTemplates.map(({ id, template }) => (
                          <div key={id} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-medium text-gray-900 truncate" title={template.metadata?.name || template.name || 'Unnamed Template'}>
                                {template.metadata?.name || template.name || 'Unnamed Template'}
                              </h4>
                              <p className="text-sm text-gray-600 truncate" title={template.metadata?.description || template.description || 'No description available'}>
                                {template.metadata?.description || template.description || 'No description available'}
                              </p>
                              <div className="flex items-center mt-1 space-x-4 text-xs text-gray-500">
                                <span>Created: {template.metadata?.createdAt ? 
                                  new Date(template.metadata.createdAt.seconds * 1000).toLocaleDateString() : 
                                  'Unknown'
                                }</span>
                                <span>Updated: {template.metadata?.updatedAt ? 
                                  new Date(template.metadata.updatedAt.seconds * 1000).toLocaleDateString() : 
                                  'Unknown'
                                }</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {Object.keys(filteredTemplates).length} template{Object.keys(filteredTemplates).length !== 1 ? 's' : ''} found
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
                            Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminTemplateBrowser;
